import { useMemo, useState } from 'react';
import { ChartBarIcon, WarningCircleIcon } from '@phosphor-icons/react';

import { formatDayLabel, resolveRange, useTeamUsage, RANGE_PRESETS, type RangePreset } from '@/api-models/usage';
import { useTeamBalance } from '@/api-models/balance';
import { useTeamMembers } from '@/api-models/teams';
import { Card } from '@/ui/Card';
import { EmptyState } from '@/ui/EmptyState';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { SpinnerBlock } from '@/ui/Spinner';
import { BudgetCard } from '@/features/settings/components/usage/BudgetCard';
import { CreditsCard } from '@/features/settings/components/usage/CreditsCard';
import { UsageBreakdown } from '@/features/settings/components/usage/UsageBreakdown';
import { UsageChart } from '@/features/settings/components/usage/UsageChart';
import { UsageLedger } from '@/features/settings/components/usage/UsageLedger';
import { UsageSummary } from '@/features/settings/components/usage/UsageSummary';
import { useSpace } from '@/contexts/space-context';

/** Sentinel for "no member filter" — Radix Select has no empty-string value. */
const ALL_MEMBERS = '__all__';

/**
 * Usage & spend for the active space.
 *
 * Everything on this page is money first: what the window cost, how that cost
 * moved day to day, and who or what it is attributable to. The figures come
 * straight from the append-only usage ledgers, so this page and an invoice are
 * reading the same rows.
 */
export function UsagePage() {
  const { teamId, teams } = useSpace();
  const team = teams.find((t) => t.id === teamId);
  const [preset, setPreset] = useState<RangePreset>('30d');
  const [member, setMember] = useState<string>(ALL_MEMBERS);
  const { members } = useTeamMembers(teamId);
  const { balance } = useTeamBalance(teamId);

  // Pinned per preset so a re-render mid-day doesn't silently shift the window
  // (and refetch) as the clock rolls over.
  const range = useMemo(() => resolveRange(preset), [preset]);
  const userId = member === ALL_MEMBERS ? null : member;
  const { usage, error, isLoading } = useTeamUsage(teamId, { ...range, userId });

  const showMemberFilter = members.length > 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="page-title">Usage & spend</h1>
          <p className="page-subtitle">
            {formatDayLabel(range.start, { withYear: true })} – {formatDayLabel(range.end, { withYear: true })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {showMemberFilter && (
            <Select value={member} onValueChange={setMember}>
              <SelectTrigger className="h-8 w-48 text-xs" aria-label="Filter by member">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_MEMBERS}>All members</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <SegmentedControl value={preset} onChange={setPreset} options={RANGE_PRESETS} ariaLabel="Date range" />
        </div>
      </div>

      {/* Both outside the load/empty branches below: a space with no spend yet is
          exactly when someone wants to set the cap — and a space that is out of
          credit has no spend to show but very much needs to be told why. */}
      {balance && <CreditsCard balance={balance} />}
      {team && <BudgetCard team={team} />}

      {error ? (
        <EmptyState
          icon={WarningCircleIcon}
          title="Could not load usage"
          description="The usage ledger could not be read for this period. Try a shorter range or reload the page."
        />
      ) : isLoading && !usage ? (
        <SpinnerBlock message="Reading the usage ledger…" />
      ) : !usage || (usage.total_micro_usd === 0 && usage.request_count === 0) ? (
        <EmptyState
          icon={ChartBarIcon}
          title="No usage in this period"
          description="Spend appears here once agent sessions start making model calls. Try a wider date range."
        />
      ) : (
        <>
          <Card radius="xl" padding="md" className="flex flex-col gap-7">
            <UsageSummary usage={usage} />
            <UsageChart usage={usage} />
          </Card>

          <UsageBreakdown usage={usage} teamId={teamId} />
          <UsageLedger usage={usage} teamId={teamId} userId={userId} />
        </>
      )}
    </div>
  );
}
